import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, UNSAFE_LocationContext, useLocation, useMatch, useNavigationType, useOutlet, type Location } from "react-router-dom";

import {
  Toaster,
  AppSidebar,
  SidebarInset,
  SidebarProvider,
  Navbar,
  AICopilotFloating,
} from "@/components/ui";
import {
  PaymentStatusNotification,
  UpgradeBanner,
  FeatureLimitNotification,
  WelcomeModal,
} from "@/components/common";
import { CookieBanner, useCookieConsent } from "@/components/legal";
import { useSidebarPreference } from "@/hooks";
import { ConnectionBanner } from "@/components/common/ConnectionBanner";
import { PilotBanner } from "@/components/pilot/PilotBanner";
import packageInfo from "../../package.json";
import { LazyRouteBoundary } from "@/routes/lazy-route-boundary";

import { lazyWithRetry } from "@/routes/lazy-with-retry";
import { isTeamWorkspaceEntry, isTeamWorkspacePath } from "@/lib/team-workspace-routes";
import { TeamWorkspaceNavigation } from "@/components/team-workspace/TeamWorkspaceNavigation";
import { TeamWorkspaceWindow } from "@/components/team-workspace/TeamWorkspaceWindow";
import { TeamWorkspaceToggle } from "@/components/team-workspace/TeamWorkspaceToggle";
const TeamWorkspace = lazyWithRetry(() => import("@/components/team-workspace/TeamWorkspaceSurface"), "TeamWorkspaceSurface");
import { useTeamWorkspaceSession } from "@/stores/use-team-workspace-session";

export function Layout() {
  const scrollRef = useRef<HTMLElement>(null);
  const clinicPath = useRef<string | null>(null);
  const outlet = useOutlet();
  const navigationType = useNavigationType();
  const [backdrop, setBackdrop] = useState<{ outlet: ReactNode; location: Location; navigationType: ReturnType<typeof useNavigationType>; scope: string | null } | null>(null);
  const location = useLocation();
  const { pathname } = location;
  const team = useTeamWorkspaceSession();
  const { update: updateTeam } = team;
  useLayoutEffect(() => {
    if (!isTeamWorkspaceEntry(pathname)) updateTeam({ returnTo: pathname + location.search + location.hash });
    else if (isTeamWorkspacePath(pathname)) {
      const redirect = location.state?.workspaceRedirect as { from: string; returnTo: string } | undefined;
      updateTeam({ workspaceTo: pathname + location.search + location.hash,
        ...(redirect?.from === team.returnTo ? { returnTo: redirect.returnTo } : {}) });
    }
  }, [pathname, location.search, location.hash, location.state, team.returnTo, updateTeam]);
  useLayoutEffect(() => {
    if (isTeamWorkspaceEntry(pathname) || clinicPath.current === pathname) return;
    clinicPath.current = pathname;
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollRef.current.scrollLeft = 0;
    }
  }, [pathname]);
  const isTeamWorkspace = isTeamWorkspacePath(pathname);
  // Keep the clinic outlet and its route context mounted, in memory and bound
  // to the authenticated identity; the workspace route owns only the dialog.
  useLayoutEffect(() => {
    if (!isTeamWorkspaceEntry(pathname) && !team.blocked && (backdrop?.location.key !== location.key || backdrop.scope !== team.scope)) {
      setBackdrop({ outlet, location, navigationType, scope: team.scope });
    }
  }, [pathname, team.blocked, team.scope, location, navigationType, outlet, backdrop]);
  const validBackdrop = backdrop?.scope === team.scope && !team.blocked ? backdrop : null;
  const clinicLocation = isTeamWorkspace ? validBackdrop?.location ?? { ...location, pathname: "/", search: "", hash: "" } : location;
  const isInbox = useMatch("/inbox") !== null || (isTeamWorkspace && clinicLocation.pathname === "/inbox");
  const isConversationWorkspace = isInbox;
  const { reopen, hydrated, hasDecided } = useCookieConsent();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebarPreference();

  // The cookie notice is a legal decision and the welcome tour is a modal that
  // traps focus over it. Only one of the two is on screen at a time: the tour
  // waits for the cookie choice.
  const welcomeTourCanOpen = hydrated && hasDecided;

  return (
    <TeamWorkspaceNavigation key={team.scope}><SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen} className="h-dvh min-h-0 overflow-hidden">
      <div className="flex min-h-0 w-full overflow-hidden bg-app-bg">
        <a
          href="#main-content"
          className="fixed left-4 top-3 z-[100] -translate-y-20 rounded-lg bg-petrol px-4 py-2 text-sm font-semibold text-white transition-transform focus:translate-y-0 focus:outline-none focus:ring-2 focus:ring-brand"
        >
          Pular para o conteúdo
        </a>
        <AppSidebar />
        <SidebarInset>
          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            <Navbar />
            <div className="shrink-0"><ConnectionBanner /><PilotBanner /></div>
            <main
              id="main-content"
              ref={scrollRef}
              tabIndex={-1}
              className={isConversationWorkspace ? "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-auto overscroll-contain bg-app-bg outline-none" : "relative flex min-h-0 min-w-0 flex-1 flex-col justify-between overflow-auto overscroll-contain bg-app-bg outline-none"}
            >
              <section className={isConversationWorkspace ? "mx-auto flex min-h-[28rem] w-full max-w-[1600px] flex-1 shrink-0 flex-col overflow-hidden px-4 pt-5 lg:px-6" : "mx-auto w-full min-w-0 max-w-[1600px] flex-1 px-4 pt-5 pb-24 lg:px-6"}>
                <UNSAFE_LocationContext.Provider value={{ location: clinicLocation, navigationType: isTeamWorkspace ? validBackdrop?.navigationType ?? navigationType : navigationType }}>
                  <PaymentStatusNotification /><UpgradeBanner />
                  <LazyRouteBoundary>
                    {isTeamWorkspace ? validBackdrop?.outlet ?? <div className="py-12"><h1 className="font-display text-3xl text-app-fg-strong">Sua clínica</h1><p className="mt-2 text-sm text-app-fg-muted">Minimize o espaço da equipe para voltar à gestão.</p></div> : outlet}
                  </LazyRouteBoundary>
                </UNSAFE_LocationContext.Provider>
              </section>
              <footer className={`mx-auto flex w-full max-w-[1600px] flex-row flex-wrap items-center gap-x-5 gap-y-3 px-4 pt-6 pb-[calc(6rem+var(--app-dock-height,0px)+env(safe-area-inset-bottom))] text-app-fg-muted sm:justify-between sm:pr-44 sm:pb-[calc(5rem+var(--app-dock-height,0px)+env(safe-area-inset-bottom))] lg:pl-6 ${isConversationWorkspace ? "shrink-0" : ""}`}>
                <p className="text-xs">
                  CliniSharp © {" "}
                  {new Date().getFullYear()}
                </p>
                <nav aria-label="Informações legais" className="flex flex-wrap items-center gap-3 text-xs">
                  <Link
                    to="/legal/privacidade"
                    className="inline-flex min-h-11 items-center rounded-md transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    Privacidade
                  </Link>
                  <span aria-hidden>•</span>
                  <Link
                    to="/legal/termos"
                    className="inline-flex min-h-11 items-center rounded-md transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    Termos
                  </Link>
                  <span aria-hidden>•</span>
                  <button
                    type="button"
                    onClick={() => reopen()}
                    className="inline-flex min-h-11 items-center rounded-md transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    Cookies
                  </button>
                </nav>
                <p className="text-sm">Versão {packageInfo.version}</p>
              </footer>
              <AICopilotFloating hideLauncher={isTeamWorkspace} />
              {!isTeamWorkspace && <TeamWorkspaceToggle />}
              {isTeamWorkspace && <TeamWorkspaceWindow key={team.scope}><LazyRouteBoundary todayAction={false}><TeamWorkspace /></LazyRouteBoundary></TeamWorkspaceWindow>}
              <FeatureLimitNotification />
              {welcomeTourCanOpen && <WelcomeModal />}
              <CookieBanner />
              <Toaster />
            </main>
          </section>
        </SidebarInset>
      </div>
    </SidebarProvider></TeamWorkspaceNavigation>
  );
}
